import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { useCurrentCompanyId } from 'utils/hooks';
import {
  DocumentationFile_Insert_Input_,
  useAddFileMutation,
  useRemoveFileMutation,
  useCompanyFilesQuery,
  CompanyFilesDocument_,
  ActivityAssessmentDocument_,
  QuestionFieldsFragmentDoc_,
} from 'models';
import { useCallback, useMemo } from 'react';
import { omitMetaOther } from 'utils';
import mixpanel from 'mixpanel-browser';
import { TRACKING_EVENTS } from 'utils/mixpanel';
import db from 'mime-db';
import { useTranslation } from 'utils/translation';
import { nhost } from 'Providers/nhost/NhostProvider.tsx';

export type UpsertFileProps = DocumentationFile_Insert_Input_ & {
  file?: File;
};

const uploadFileSecurely = async (file: File) => {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const payload = {
    file: {
      name: file.name,
      type: file.type,
      size: file.size,
      data: base64,
    },
    bucketId: 'default',
  };

  try {
    console.log('calling');
    const response = await nhost.functions.post<{ fileId: string; fileName: string }>(
      '/upload-file',
      payload
    );
    return {
      fileMetadata: { id: response.body.fileId, name: response.body.fileName },
      error: null,
    };
  } catch (error) {
    return {
      fileMetadata: null,
      error,
    };
  }
};

const handleUpsertBinaryFile = async (updateObj: UpsertFileProps & { file: File }) => {
  const uploadedFile = await uploadFileSecurely(updateObj.file);
  return uploadedFile;
};

export function useUpsertFile() {
  const [saveFile] = useAddFileMutation();
  const { companyId } = useCurrentCompanyId();
  const { user } = useAuth();
  const userId = user?.id;
  const { t } = useTranslation('common');

  return useCallback(
    async (updateObj: UpsertFileProps) => {
      let storageFile;

      if (!!updateObj.file) {
        const { fileMetadata } = await handleUpsertBinaryFile(
          updateObj as UpsertFileProps & { file: File }
        );
        storageFile = fileMetadata;
      }

      try {
        const { data } = await saveFile({
          variables: {
            input: {
              title: storageFile?.name ?? t('common:unknown'),
              tags: [],
              ...(storageFile?.id ? { storageFileId: storageFile?.id } : {}),
              ...omitMetaOther(updateObj, 'uploadedBy', 'updatedAt', 'file'),
              companyId,
            },
          },
          refetchQueries: [CompanyFilesDocument_],
        });
        return data?.file;
      } catch (error) {
        console.log(error);
      }
    },
    [saveFile, companyId, userId]
  );
}

export function useDeleteFile() {
  const { companyId } = useCurrentCompanyId();
  const [deleteDocumentationFile] = useRemoveFileMutation();
  return useCallback(
    async ({ id, storageFileId }: { id: string; storageFileId: string }) => {
      mixpanel.track(TRACKING_EVENTS.DRIVE.DELETE, {
        companyId,
        fileId: id,
      });
      await nhost.storage.deleteFile(storageFileId);
      const { data: deletedFileData } = await deleteDocumentationFile({
        variables: { id },
        refetchQueries: [
          CompanyFilesDocument_,
          ActivityAssessmentDocument_,
          QuestionFieldsFragmentDoc_,
        ],
      });

      return deletedFileData?.file?.id;
    },
    [deleteDocumentationFile]
  );
}

export const useCompanyFiles = () => {
  const { companyId } = useCurrentCompanyId();
  const { data, loading } = useCompanyFilesQuery({
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  const files = useMemo(() => {
    return data?.files ?? [];
  }, [data?.files]);

  return { files, loading };
};

export const useFileExtension = (mimeType?: string) => {
  const extension = useMemo(() => {
    return db[mimeType ?? '']?.extensions?.[0];
  }, [mimeType]);
  return extension;
};
